import { useCallback, useEffect, useMemo, useState } from 'react';
import { usePermissionStore } from 'src/models/usePermissionModel';
import { DlpPolicyItem, TabsEnum } from 'src/pages/Policy/utils/types';
import {
  storeSelector,
  templateSelector,
  usePolicyContext,
} from '@Policy/store';
import { withEditPermission } from 'src/utils/common';
import { useNavigateWithHandler } from 'src/utils/hooks';
import { PermissionNameEnum } from '@Types/permissions';
import { PolicyTypeEnum } from '@Policy/utils/constants';
import { PolicyShell } from '@Policy/PolicyShell';

export const DlpPolicy = (props: { tabs: TabsEnum[]; tabPanels: any[] }) => {
  const { tabs, tabPanels } = props;
  const navigate = useNavigateWithHandler();

  const permissionModel = usePermissionStore();
  // get store from context
  const contextStore = usePolicyContext();
  // get store selector
  const contextSelector = storeSelector(contextStore);
  const policySelector = templateSelector(contextStore)<DlpPolicyItem>;

  const policyMode = policySelector((s) => s.policyMode);
  const policyType = policySelector((s) => s.policyType);
  const policySubType = policySelector((s) => s.policySubType);
  const policyId = policySelector((s) => s.policyUuid);
  const tmDpfiltersEnabled = policySelector((s) => s.tmDpfilters.dpEnabled);
  const tmKeywordExtractionEnabled = policySelector(
    (s) => s.tmKeywordfilters?.kfEnabled
  );
  const tmShareLinkEnabled = true;
  const activeTabIndex = contextSelector((s) => s.activeTabIndex);
  const setActiveTabIndex = contextSelector((s) => s.setActiveTabIndex);
  const getRecipientGroups = contextSelector((s) => s.getRecipientGroups);
  const submitPolicy = contextSelector((s) => s.submitPolicy);

  const [isLoading, setIsLoading] = useState(false);

  const tabStatusArr = useMemo(() => {
    if (policyType === PolicyTypeEnum.Box) {
      if (policySubType === 1) {
        return [null, tmShareLinkEnabled];
      } else {
        return [null, tmDpfiltersEnabled, tmKeywordExtractionEnabled];
      }
    } else {
      return [null, tmDpfiltersEnabled];
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    tmDpfiltersEnabled,
    tmKeywordExtractionEnabled,
    policyType,
    policySubType,
  ]);

  const saveDisabled = useMemo(() => {
    return (
      !withEditPermission(
        PermissionNameEnum.dlpPolicy,
        permissionModel.permissions
      ) || policyMode === 3
    );
  }, [permissionModel.permissions, policyMode]);

  const handleSubmitPolicy = useCallback(async () => {
    setIsLoading(true);
    const successCallback = () => {
      const category = [1, 21, 22, 82].includes(policyType)
        ? 'email'
        : 'collaboration';
      const dlpPolicyListUrl = `/policy/data-loss-prevention/${category}`;
      navigate(dlpPolicyListUrl);
    };

    await submitPolicy({
      successCallback,
      policyId,
    });

    setIsLoading(false);
  }, [navigate, policyId, policyType, submitPolicy]);

  const goBack = useCallback(() => {
    const category = [1, 21, 22, 82].includes(policyType)
      ? 'email'
      : 'collaboration';
    const dlpPolicyListUrl = `/policy/data-loss-prevention/${category}`;
    navigate(dlpPolicyListUrl);
  }, [navigate, policyType]);

  useEffect(() => {
    getRecipientGroups();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <PolicyShell
      tabs={tabs}
      tabPanels={tabPanels}
      tabStatusArr={tabStatusArr}
      activeTabIndex={activeTabIndex}
      setActiveTabIndex={setActiveTabIndex}
      handleSubmitPolicy={handleSubmitPolicy}
      saveDisabled={saveDisabled}
      isLoading={isLoading}
      goBack={goBack}
      policyType={policyType}
    />
  );
};
